import { Activity, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Division,
  Flex,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Span,
  EmptyState,
  Row,
  Col,
  useToast,
} from "@ir/ui";
import { IPromoDeductionMatchSummary } from "@/core/repositories/promotion";
import {
  formatConfidence,
  formatMatchStatusLabel,
  getAppliedMatch,
  getMatchStatusChipColor,
  getMatchStatusChipColorForMatch,
  getMatchStatusChipLabel,
  matchCriteriaToDisplayRows,
  resolveInitialSelectedMatchId,
  shouldShowApproveApplyCTA,
} from "./promoMatch/promoMatch.utils";
import { Deduction } from "@/core/entities/deduction";
import { useUserPermissions } from "@/hooks";
import { WorkspacePermissions } from "@/core/permissions";
import { useAtomValue, useSetAtom } from "jotai";
import {
  fetchPromoMatchSummariesAtom,
  promoMatchByDeductionIdAtom,
  putPromoDeductionAppliedAtom,
} from "@/store/promotion";
import { isDetailsUpdatingAtom, isIntelligenceUpdatingAtom } from "@/store/deduction/deduction";
import ApplyPromoMatchModal from "./promoMatch/ApplyPromoMatchModal";

const PromoMatchCriteria = ({ match }: { match: IPromoDeductionMatchSummary }) => {
  const criteria = matchCriteriaToDisplayRows(match);

  return (
    <Division className="tpm-auto-match-criteria p-2" style={{minWidth: 250}}>
      <Paragraph className="text-grey-500 text-xs mb-2">Match Criteria:</Paragraph>
      {criteria.map(criterion => (
        <Flex
          key={criterion.label}
          gap={0.5}
          justifyContent="start"
          align="start"
          wrap="no-wrap"
          className="mb-1 tpm-auto-match-criteria-row"
        >
          <Icon
            name={criterion.aligned ? Icons.Check : Icons.Close}
            size="small"
            className={criterion.aligned ? "text-success-500" : "text-error-500"}
          />
          <Span className="text-grey-600 text-xs">{criterion.label}:</Span>
          <Span className={`text-xs font-medium ${criterion.aligned ? "text-success-600" : "text-error-600"}`}>
            {criterion.value}
          </Span>
        </Flex>
      ))}
    </Division>
  );
};

const PromoMatchPrimaryPanel = ({
  match,
  showApproveApply,
  onApproveApply,
  isApplying,
}: {
  match: IPromoDeductionMatchSummary;
  showApproveApply: boolean;
  onApproveApply: () => void;
  isApplying: boolean;
}) => {
  const { matchSummary, promotion } = match;
  const confidence = formatConfidence(matchSummary.confidenceScore);
  const chipColor = getMatchStatusChipColorForMatch(match);
  const { hasAnyPermission } = useUserPermissions();
  
  return (
    <Division className="h-100 bg-white tpm-auto-match-primary border-0 border-color-grey-50 me-3 tpm-auto-match-primary--best">
      <Flex justifyContent="space-between" align="center" className="p-3">
        <Flex align="center" gap={1}>
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Promotions
          </Heading>
        </Flex>
        <Flex align="center" gap={1}>
          <Chip size="small" color={chipColor} label={getMatchStatusChipLabel(match)} borderless />
          <Span className={`text-${chipColor}-600 text-lg font-medium`}>{confidence}%</Span>
        </Flex>
      </Flex>
      <Flex align="stretch" justifyContent="space-between" direction="row" gap={2} wrap="no-wrap" style={{minWidth: 150}}>
        <Flex direction="column" justifyContent="start" align="start" className="p-3">
          <Flex direction="column">
            <Flex direction="row" align="center" gap={1} wrap="no-wrap">
              <Heading level={6} className="text-grey-600 font-bold m-0">
                {promotion.promoName}{" "}
              </Heading>
            </Flex>
            <Flex gap={0.5} wrap="wrap" className="my-2">
              <Chip borderless={true} size="small" color="grey" label={promotion.type.promoTypeName} />
            </Flex>
            <Flex direction="column" justifyContent="start" align="start" className="mb-1">
              {match?.promotion?.promoStartDate && match?.promotion?.promoEndDate && (
                <Row align="flex-start" wrap={false}>
                  <Col>
                    <Span className="text-grey-500 text-xs">Period:</Span>
                  </Col>
                  <Col>
                    <Span className="text-grey-700 text-xs font-bold">
                      {match.promotion.promoStartDate} - {match.promotion.promoEndDate}
                    </Span>
                  </Col>
                </Row>
              )}
              {match?.promotion?.notes && (
                <Row align="flex-start" wrap={false} className="mb-1">
                  <Col>
                    <Span className="text-grey-500 text-xs">Notes:</Span>
                  </Col>
                  <Col>
                    <Span className="text-grey-700 text-xs font-bold">{match?.promotion?.notes}</Span>
                  </Col>
                </Row>
              )}
            </Flex>
          </Flex>
          { showApproveApply && hasAnyPermission([WorkspacePermissions.DEDUCTIONS_EDIT, WorkspacePermissions.TRADE_PROMOTIONS_EDIT]) && (
            <Button
              variant="solid"
              color="primary"
              size="small"
              className="tpm-auto-match-apply-btn mt-2"
              onPress={onApproveApply}
              isDisabled={isApplying}
            >
              Approve &amp; Apply
            </Button>
          )}
        </Flex>
        <PromoMatchCriteria match={match} />
      </Flex>
    </Division>
  );
};

const PromoMatchAlternativeItem = ({
  match,
  deduction,
  onSelect,
}: {
  match: IPromoDeductionMatchSummary;
  deduction: Deduction;
  onSelect: (matchResultId: number) => void;
}) => {
  const { hasAnyPermission } = useUserPermissions();
  const canSelect = hasAnyPermission([WorkspacePermissions.DEDUCTIONS_EDIT, WorkspacePermissions.TRADE_PROMOTIONS_EDIT]) && deduction.canSelectAutoMatch();
  return (
    <button
      type="button"
      className="tpm-auto-match-alternative-item mb-2 w-100 border-1 border-color-grey-25 p-2 rounded-small bg-grey-10"
      disabled={!canSelect}
      onClick={canSelect ? () => onSelect(match.matchResultId) : undefined}
    >
      <Flex direction="column" gap={0.15} wrap="no-wrap" className="flex-1 w-100">
        <Flex
          direction="row"
          justifyContent="space-between"
          align="stretch"
          gap={0.15}
          wrap="no-wrap"
          className="w-100"
        >
          <Flex direction="column" justifyContent="start" align="start">
            <Span className="text-grey-700 text-xs font-bold text-left small">{match.promotion.promoName}</Span>
            <Flex justifyContent="space-between" align="center">
              <Chip
                borderless={true}
                size="small"
                color="grey"
                label={match?.promotion?.type?.promoTypeName || "Unknown"}
                className="text-xs font-medium bg-grey-25 lh-1 my-1"
              />
            </Flex>
            {match?.promotion?.promoStartDate && match?.promotion?.promoEndDate && (
              <Row align="flex-start" wrap={false} className="text-left">
                <Col className="pe-0">
                  <Span className="text-grey-500 text-xs small">Period:</Span>
                </Col>
                <Col>
                  <Span className="text-grey-700 text-xs font-bold text-left small">
                    {match.promotion.promoStartDate} - {match.promotion.promoEndDate}
                  </Span>
                </Col>
              </Row>
            )}
          </Flex>
          <Flex direction="column" justifyContent="space-between" align="end" style={{width: 100}}>
            <Chip
              size="small"
              color={getMatchStatusChipColor(match.matchSummary.matchStatus)}
              borderless={true}
              label={`${formatConfidence(match.matchSummary.confidenceScore)}%`}
              className="tpm-auto-match-confidence-chip"
            />
            <Chip
            size="small"
            color={getMatchStatusChipColor(match.matchSummary.matchStatus)}
            label={formatMatchStatusLabel(match.matchSummary.displayMatchStatus)}
            borderless
            className="small"
          />
          </Flex>
        </Flex>
      </Flex>
    </button>
  );
};

const emptyPromoMatchState = {
  summaries: [] as IPromoDeductionMatchSummary[],
  isLoading: false,
  isApplying: false,
  error: null as Error | null,
};

const DeductionTPMAutoMatch = ({ deduction }: { deduction?: Deduction | null }) => {
  const deductionId = deduction?.id;
  const promoMatchByDeductionId = useAtomValue(promoMatchByDeductionIdAtom);
  const {
    summaries: matches,
    isLoading,
    isApplying,
    error,
  } = (deductionId === null ? undefined : promoMatchByDeductionId[deductionId!]) ?? emptyPromoMatchState;
  const fetchPromoMatchSummaries = useSetAtom(fetchPromoMatchSummariesAtom);
  const putPromoDeductionApplied = useSetAtom(putPromoDeductionAppliedAtom);
  const isDeductionDetailsUpdating = useAtomValue(isDetailsUpdatingAtom);
  const isDeductionIntelligenceUpdating = useAtomValue(isIntelligenceUpdatingAtom);
  const { hasAnyPermission } = useUserPermissions();
  const toast = useToast();

  const [selectedMatchResultId, setSelectedMatchResultId] = useState<number | null>(null);
  const [panelAnimKey, setPanelAnimKey] = useState(0);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  const canEdit = hasAnyPermission([WorkspacePermissions.DEDUCTIONS_EDIT]);
  const canSelect = deduction?.canSelectAutoMatch() ?? false;

  const primaryMatch = useMemo(() => {
    if (selectedMatchResultId == null) {
      return null;
    }
    return matches.find(match => match.matchResultId === selectedMatchResultId) ?? null;
  }, [matches, selectedMatchResultId]);

  const alternatives = useMemo(() => {
    if (selectedMatchResultId == null) {
      return [];
    }
    return matches.filter(match => match.matchResultId !== selectedMatchResultId);
  }, [matches, selectedMatchResultId]);

  const appliedMatch = useMemo(() => getAppliedMatch(matches), [matches]);

  const showApproveApply = useMemo(
    () =>
      shouldShowApproveApplyCTA({
        matches,
        primaryMatch,
        canEdit,
        canSelect,
        isApplying,
      }),
    [matches, primaryMatch, canEdit, canSelect, isApplying]
  );

  const loadMatches = useCallback(
    (refetch: boolean) => {
      if (deductionId == null) {
        return;
      }
      fetchPromoMatchSummaries({ deductionId, refetch })
        .then(fetchedMatches => {
          setSelectedMatchResultId(resolveInitialSelectedMatchId(fetchedMatches));
        })
        .catch(() => {
          setSelectedMatchResultId(null);
        });
    },
    [deductionId, fetchPromoMatchSummaries]
  );

  useEffect(() => {
    loadMatches(false);
  }, [loadMatches]);

  // Deduction edits (details/intelligence) can trigger backend auto-matching that
  // links/relinks a promotion. The deductionId doesn't change on save, so re-fetch
  // (bypassing the cache) once a save transitions from in-progress back to idle.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    const isSaving = isDeductionDetailsUpdating || isDeductionIntelligenceUpdating;
    if (wasSavingRef.current && !isSaving) {
      loadMatches(true);
    }
    wasSavingRef.current = isSaving;
  }, [isDeductionDetailsUpdating, isDeductionIntelligenceUpdating, loadMatches]);

  const handleSelectAlternative = useCallback((matchResultId: number) => {
    setSelectedMatchResultId(matchResultId);
    setPanelAnimKey(key => key + 1);
  }, []);

  const handleOpenApplyModal = useCallback(() => {
    setIsApplyModalOpen(true);
  }, []);

  const handleCloseApplyModal = useCallback(() => {
    setIsApplyModalOpen(false);
  }, []);

  const handleConfirmApply = useCallback(
    async (reversalReason?: string) => {
      if (deductionId == null || selectedMatchResultId == null) {
        return;
      }

      try {
        const updatedMatches = await putPromoDeductionApplied({
          deductionId,
          matchResultId: selectedMatchResultId,
          reversalReason,
        });
        const applied = getAppliedMatch(updatedMatches);
        if (applied) {
          setSelectedMatchResultId(applied.matchResultId);
        }
        setIsApplyModalOpen(false);
        toast.success({ content: "Promotion applied to deduction" });
      } catch (applyError) {
        toast.error({
          content: applyError instanceof Error ? applyError.message : "Failed to apply promotion",
        });
      }
    },
    [deductionId, selectedMatchResultId, putPromoDeductionApplied, toast]
  );

  if (!deduction?.id) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-2"
        icon={Icons.InfoOctagon}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            Error fetching promo matches
          </Heading>
        }
        description={<Paragraph className="text-grey-500 font-regular mb-0">{error.message}</Paragraph>}
      />
    );
  }

  if (matches.length === 0) {
    return (
      <Division className="bg-error-100 border-1 border-color-error-200 division mb-3 p-3 rounded-small">
        <Paragraph className="text-error-500 font-regular mb-0">No promotion matches found for this deduction.</Paragraph>
      </Division>
    );
  }

  if (!primaryMatch) {
    return (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-2"
        icon={Icons.File}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            No Matches Found
          </Heading>
        }
        description={
          <Paragraph className="text-grey-500 font-regular mb-0">No matches found for this deduction.</Paragraph>
        }
      />
    );
  }

  return (
    <>
      <ApplyPromoMatchModal
        isOpen={isApplyModalOpen}
        onClose={handleCloseApplyModal}
        promoName={primaryMatch.promotion.promoName}
        isApplying={isApplying}
        showReversalReason={Boolean(appliedMatch)}
        onConfirm={handleConfirmApply}
      />
      <Card className="mb-3 tpm-auto-match-card">
        <CardContent className="p-0">
          <Flex className="tpm-auto-match-layout" align="stretch">
            <Division
              key={panelAnimKey}
              className={`tpm-auto-match-primary-transition${panelAnimKey > 0 ? " tpm-auto-match-primary-transition--animate" : ""}`}
            >
              <PromoMatchPrimaryPanel
                match={primaryMatch}
                showApproveApply={showApproveApply}
                onApproveApply={handleOpenApplyModal}
                isApplying={isApplying}
              />
            </Division>

            <Division className="bg-white tpm-auto-match-alternatives p-3 border-0 border-color-grey-50">
              <Flex justifyContent="space-between" align="center" className="mb-3">
                <Flex direction="column">
                  <Heading level={6} className="text-grey-700 text-sm font-bold m-0 lh-1-5">
                    Other Possible Matches
                  </Heading>
                  <Activity mode={canSelect ? "visible" : "hidden"}>
                    <Span className="text-grey-500 font-medium text-xs lh-1">Click to override</Span>
                  </Activity>
                </Flex>
                <Chip
                  size="small"
                  color="grey"
                  borderless={true}
                  label={`${alternatives.length} alternative${alternatives.length === 1 ? "" : "s"}`}
                />
              </Flex>

              <Division className="tpm-auto-match-alternatives-scroll bg-white">
                {alternatives.length === 0 ? (
                  <Division className="text-grey-500 text-sm py-2">No alternative matches available.</Division>
                ) : (
                  alternatives.map(alternative => (
                    <PromoMatchAlternativeItem
                      key={alternative.matchResultId}
                      match={alternative}
                      deduction={deduction}
                      onSelect={handleSelectAlternative}
                    />
                  ))
                )}
              </Division>
            </Division>
          </Flex>
        </CardContent>
      </Card>
    </>
  );
};

DeductionTPMAutoMatch.displayName = "DeductionTPMAutoMatch";

export default memo(DeductionTPMAutoMatch);
